import { ENV } from '../../../../../config/runtimeEnv';
import React, { useState, useEffect } from "react";
import { getSessionUser } from "../../../../../utils/auth/sessionUser";
import { Modal, Button, Space, Typography, Segmented, Select } from "antd";
import { PlusCircleOutlined } from "@ant-design/icons";

import { AssignedForm, assignForm, fetchForms, Form, FormElement } from "../../../../../api/formApi";
import { Patient } from "../../../../../types/healthcare/PatientTypes";
import { TeamMember } from "../../../../../types/admin/TeamTypes";
import { User } from "../../../../../types/admin/UserTypes";
import { sendCommunication } from "../../../../../api/communications/communicationApi";
import { Company } from "../../../../../types/admin/CompanyTypes";
import { fetchTeamMembers } from "../../../../../api/admin/teamApi";
import { useNotifications } from "../../../../../context/NotificationContext";
import { DataTable, SectionCard } from "../../../../../components/shared";
import { buildFormAssignedNotification } from "../../../../../utils/forms/formAssignedNotification";
import type { UserUuid } from "../../../../../types/identity";

const { Text } = Typography;

type AssigneeMode = 'patient' | 'practitioner';

interface AssignFormModalProps {
	isOpen: boolean;
	onClose: () => void;
	patient: Patient;
	company: Company;
}

const AssignFormModal: React.FC<AssignFormModalProps> = ({ isOpen, onClose, patient, company }) => {
	const user: User = getSessionUser();

	const [mode, setMode] = useState<AssigneeMode>('patient');
	const [availableForms, setAvailableForms] = useState<Form[]>();
	const [selectedForms, setSelectedForms] = useState<Form[]>([]);
	const [practitioners, setPractitioners] = useState<TeamMember[]>([]);
	const [selectedPractitionerId, setSelectedPractitionerId] = useState<UserUuid | null>(null);
	const [isLoading, setIsLoading] = useState(false);
	const [searchTerm, setSearchTerm] = useState("");

	const { showError, showSuccess, showWarning } = useNotifications();

	useEffect(() => {
		if (isOpen) {
			loadForms();
			loadPractitioners();
		}
	}, [isOpen, mode]);

	const loadForms = async () => {
		setIsLoading(true);
		try {
			const { data } = await fetchForms(searchTerm, mode === 'practitioner' ? 'practitioner' : 'patient');
			const transformed = data.map((form: Form) => ({
				...form,
				structure: typeof form.structure === "string" ? JSON.parse(form.structure) as FormElement[] : form.structure,
			}));
			setAvailableForms(transformed);
		} catch {
			showError("Failed to load forms");
		} finally {
			setIsLoading(false);
		}
	};

	const loadPractitioners = async () => {
		if (mode !== 'practitioner') return;
		try {
			const { data } = await fetchTeamMembers(1, 100);
			setPractitioners(data);
		} catch {
			showError("Failed to load practitioners");
		}
	};

	const getFilteredForms = () => {
		if (!availableForms) return [];
		if (!searchTerm) return availableForms;
		const q = searchTerm.toLowerCase();
		return availableForms.filter((f) => f.title?.toLowerCase().includes(q) || f.department?.toLowerCase().includes(q));
	};

	const handleSubmit = async (sendNotification: boolean) => {
		if (selectedForms.length === 0) {
			showWarning("Select at least one form to assign");
			return;
		}
		if (mode === 'practitioner' && !selectedPractitionerId) {
			showWarning("Select the practitioner to assign the form to");
			return;
		}

		setIsLoading(true);
		try {
			for (const form of selectedForms) {
				const af: Partial<AssignedForm> = {
						form_id: form.id,
					patient_id: patient.id,
					practitioner_id: user.id,
					notes: "",
					// F6: was "Assigned" — a value NOTHING on the backend recognised. The tray
					// status filter did not offer it, the dashboard's pending tile did not count
					// it, and the treatment-plan gates did not see it. `assigned_forms.status` now
					// has one vocabulary (`App\Enums\FormStatus`) enforced by `Rule::enum`, and
					// "Assigned" is a 422.
					status: "Pending",
					assignee_type: mode,
					assigned_to_practitioner_id: mode === 'practitioner' ? (selectedPractitionerId ?? undefined) : undefined,
					created_at: new Date(),
					updated_at: new Date(),
				};

				const response = await assignForm(af, patient.uuid);

				if (mode === 'patient' && sendNotification) {
					// `assignForm` returns healthcare's 201 body, which is shaped to carry
					// `access_token` for exactly this — the emailed link is the only copy of it
					// the patient will ever have. One shared builder with the other three send
					// sites (F11): see `utils/forms/formAssignedNotification`.
					const payload = buildFormAssignedNotification({
						assignedFormId: response.id,
						accessToken: response.access_token,
						patient,
						companyId: company.id,
					});
					if (ENV.VITE_NOTIFICATIONS_ENABLED === "true") {
						sendCommunication(payload);
					}
				}
			}

			showSuccess(`${selectedForms.length} form(s) assigned successfully`);
			setSelectedForms([]);
			setSelectedPractitionerId(null);
			onClose();
		} catch {
			showError("Failed to assign forms");
		} finally {
			setIsLoading(false);
		}
	};

	const confirmAndSubmit = () => {
		if (mode === 'patient') {
			Modal.confirm({
				title: "Send Notification?",
				content: "Would you like to send a notification to the patient about the assigned form(s)?",
				okText: "Send & Assign",
				cancelText: "Just Assign",
				onOk: () => handleSubmit(true),
				onCancel: () => handleSubmit(false),
			});
		} else {
			handleSubmit(false);
		}
	};

	const formColumns = [
		{
			title: "Form Title",
			dataIndex: "title",
			key: "title",
			sorter: (a: Form, b: Form) => (a.title || "").localeCompare(b.title || ""),
			render: (text: string, record: Form) => (
				<div>
					<div style={{ fontSize: 14, fontWeight: 500 }}>{text}</div>
					{record.department && <Text type="secondary" style={{ fontSize: 12 }}>{record.department}</Text>}
				</div>
			),
		},
		{
			title: "Type",
			dataIndex: "form_type",
			key: "form_type",
			width: 120,
			render: (v: string) => <Text type="secondary" style={{ fontSize: 12 }}>{v === 'practitioner' ? 'Practitioner' : 'Patient'}</Text>,
		},
		{
			title: "Created",
			dataIndex: "created_at",
			key: "created_at",
			width: 100,
			render: (date: Date) => {
				try { return <span style={{ fontSize: 12 }}>{new Date(date).toLocaleDateString()}</span>; }
				catch { return <span style={{ fontSize: 12 }}>—</span>; }
			},
		},
	];

	return (
		<Modal
			title={
				<Space>
					<PlusCircleOutlined style={{ color: "var(--mg-primary)" }} />
					<span>Assign Forms — {patient.first_name} {patient.last_name}</span>
				</Space>
			}
			open={isOpen}
			onCancel={onClose}
			width={860}
			destroyOnClose
			footer={[
				<Text key="count" type="secondary" style={{ fontSize: 12, marginRight: "auto" }}>
					{selectedForms.length} form(s) selected
				</Text>,
				<Button key="cancel" onClick={onClose} disabled={isLoading}>Cancel</Button>,
				<Button key="submit" type="primary" onClick={confirmAndSubmit} disabled={selectedForms.length === 0 || isLoading} loading={isLoading}>
					{`Assign${selectedForms.length > 0 ? ` (${selectedForms.length})` : ""}`}
				</Button>,
			]}
		>
			<div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
				<div>
					<Text type="secondary" style={{ fontSize: 12, display: "block", marginBottom: 8 }}>Assign to</Text>
					<Segmented
						value={mode}
						onChange={(v) => { setMode(v as AssigneeMode); setSelectedForms([]); setSelectedPractitionerId(null); }}
						options={[
							{ label: 'Patient', value: 'patient' },
							{ label: 'Practitioner', value: 'practitioner' },
						]}
					/>
				</div>

				{mode === 'practitioner' && (
					<SectionCard>
						<Space direction="vertical" style={{ width: "100%" }}>
							<Text style={{ fontSize: 13 }}>Select the practitioner who will fill this form (regarding this patient):</Text>
							<Select
								style={{ width: "100%" }}
								placeholder="Select practitioner…"
								value={selectedPractitionerId}
								onChange={setSelectedPractitionerId}
								showSearch
								optionFilterProp="label"
								options={practitioners.map((p) => ({ value: p.id, label: `${p.first_name} ${p.last_name}` }))}
							/>
						</Space>
					</SectionCard>
				)}

				<DataTable
					title={mode === 'practitioner' ? 'Practitioner Forms' : 'Patient Forms'}
					columns={formColumns}
					dataSource={getFilteredForms()}
					rowKey="id"
					loading={isLoading}
					selectedRows={selectedForms}
					setSelectedRows={setSelectedForms}
					emptyTitle="No forms available"
					pagination={{ pageSize: 10 }}
				/>

				{selectedForms.length > 0 && (
					<SectionCard>
						<Space direction="vertical" style={{ width: "100%" }}>
							<Text style={{ fontSize: 14, fontWeight: 500, color: "var(--mg-success)" }}>
								{selectedForms.length} form(s) selected
							</Text>
							{selectedForms.map((form) => (
								<div key={form.id} style={{ fontSize: 12, color: "var(--mg-success)" }}>• {form.title}</div>
							))}
						</Space>
					</SectionCard>
				)}
			</div>
		</Modal>
	);
};

export default AssignFormModal;
